"use client";

import React, { useState } from "react";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const accountTypes = [];
const priceList = [{
  size: "1,5 m²",
  price: "20€/kk",
  tier: "small"
}, {
  size: "2 m²",
  price: "30€/kk",
  tier: "small"
}, {
  size: "3 m²",
  price: "40€/kk",
  tier: "medium"
}, {
  size: "4 m²",
  price: "50€/kk",
  tier: "medium"
}, {
  size: "5 m²",
  price: "60€/kk",
  tier: "medium"
}, {
  size: "6 m²",
  price: "72€/kk",
  tier: "large"
}, {
  size: "7 m²",
  price: "84€/kk",
  tier: "large"
}, {
  size: "9 m²",
  price: "108€/kk",
  tier: "large"
}, {
  size: "10 m²",
  price: "120€/kk",
  tier: "large"
}, {
  size: "11 m²",
  price: "132€/kk",
  tier: "large"
}, {
  size: "23 m²",
  price: "250€/kk",
  tier: "large"
}, {
  size: "Muuttolaatikko",
  price: "0,10€/päivä",
  tier: "large"
}];
export default function AccountTypesSelector() {
  const [selectedId, setSelectedId] = useState<string>("professional");
  return <section data-section-id="252238" id="pricing-plans" className="bg-muted/30 text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <p className="text-center max-w-2xl mx-auto mb-12 !font-default !font-bold !text-5xl text-primary">Hinnat<br /><br /> <div className="w-24 h-1 bg-secondary mx-auto rounded-lg opacity-100" /></p>
          
        <div className="text-center mb-12 md:mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {priceList.map((item, i) => {
            const tierStyles = "border-secondary/20 bg-secondary/5 hover:bg-secondary/10";
            const priceColor = "text-secondary";
            return <Card key={i} data-index={i} className={`group transition-all duration-300 hover:scale-[1.03] hover:shadow-lg cursor-default border ${tierStyles} py-4`}>
                  <CardContent className="p-0 flex flex-col items-center justify-center gap-1">
                    <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground/70">Koko</span>
                    <span className="text-lg font-bold font-mono">{item.size}</span>
                    <Separator className="w-8 my-1 opacity-20" />
                    <span className={`text-2xl font-bold font-default ${priceColor}`}>{item.price}</span>
                    <Button variant="outline" size="sm" className="mt-2 w-full py-0 h-8 text-xs rounded-lg" asChild>
                      <Link to="/Varaa">
                        Tarkista saatavuus
                      </Link>
                    </Button>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {accountTypes.map((account, i) => {
          const isSelected = selectedId === account.id;
          return <Card key={account.id} data-index={i} onClick={() => setSelectedId(account.id)} className={`relative cursor-pointer transition-all duration-300 rounded-lg border bg-card/80 backdrop-blur-md ${isSelected ? "border-secondary ring-1 ring-secondary shadow-2xl scale-[1.02] md:-translate-y-2" : "border-border hover:border-secondary/50 shadow-md hover:-translate-y-2"}`}>
                {account.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <Badge variant="default" className="bg-secondary text-secondary-foreground rounded-lg px-4 py-1 uppercase tracking-wider text-[10px] font-bold shadow-sm">
                      Suosituin valinta
                    </Badge>
                  </div>}
                
                <CardHeader className="text-center pb-6 pt-8">
                  <CardTitle className="text-2xl font-bold font-default text-foreground">
                    {account.name}
                  </CardTitle>
                  <CardDescription className="mt-3 h-12 text-sm font-sans">
                    {account.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6 px-6">
                  <div className="text-center bg-accent/50 rounded-lg py-4">
                    <div className="text-sm text-muted-foreground mb-1 font-medium font-sans">
                      Varaston koko
                    </div>
                    <div className="text-4xl font-bold font-default text-primary">
                      {account.minDeposit}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="text-center p-3 border border-border rounded-lg bg-background/50">
                      <div className="text-muted-foreground mb-1 text-xs uppercase tracking-wider font-sans">Kuukausihinta</div>
                      <div className="font-bold text-foreground font-default">{account.spreadsFrom}</div>
                    </div>
                    <div className="text-center p-3 border border-border rounded-lg bg-background/50">
                      <div className="text-muted-foreground mb-1 text-xs uppercase tracking-wider font-sans">Kapasiteetti</div>
                      <div className="font-semibold text-foreground font-sans">{account.leverage}</div>
                    </div>
                  </div>

                  <Separator className="my-6 opacity-50" />

                  <ul className="space-y-4">
                    {accountFeatures.filter(f => f.accountId === account.id).map((feature, j) => <li key={j} data-index={j} className="flex items-start gap-3 text-sm text-muted-foreground font-sans">
                          <div className={`mt-0.5 rounded-lg p-0.5 ${isSelected ? 'bg-secondary/20 text-secondary' : 'bg-accent text-muted-foreground'}`}>
                            <Check className="w-3.5 h-3.5 shrink-0" />
                          </div>
                          <span className="leading-tight">{feature.feature}</span>
                        </li>)}
                  </ul>
                </CardContent>

                <CardFooter className="pt-6 pb-8 px-6">
                  <Button variant={isSelected ? "secondary" : "outline"} className={`w-full rounded-lg h-12 text-base font-bold transition-all font-default ${!isSelected && "hover:bg-secondary/10 hover:text-secondary hover:border-secondary/30"}`} asChild>
                    <Link to="/ContactPage">
                      {account.buttonText}
                    </Link>
                  </Button>
                </CardFooter>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}